import { PageRoutes } from 'constants/Routes';
import { BusinessMetadata } from 'custom-types';
import omit from 'lodash/omit';
import values from 'lodash/values';
import {
  BreadcrumbJsonLd,
  DefaultSeo,
  OrganizationJsonLd,
  SiteLinksSearchBoxJsonLd,
} from 'next-seo';
import { FC } from 'react';
import ImageHelpers from 'utils/helpers/ImageHelpers';

interface IShopSEOProps {
  businessMetadata: BusinessMetadata;
}

const ShopSEO: FC<IShopSEOProps> = ({ businessMetadata }) => {
  const { social, coverImage, avatarImage, title, headline, subdomain, customDomain } =
    businessMetadata;

  const cottageTld = `mycottage${process.env.NEXT_PUBLIC_STAGE === 'devo' ? '-devo.link' : '.menu'}`;
  const canonicalDomain = customDomain ?? `${subdomain}.${cottageTld}`;
  const canonicalUrl = `https://${canonicalDomain}`;

  const coverImageUrl = ImageHelpers.resolveImage(
    'assets/shop/default_cover_image.png',
    coverImage
  );

  const avatarImagePathOrUrl = avatarImage
    ? ImageHelpers.resolveAssetImage(avatarImage)
    : '/favicon.ico';
  const avatarImageAbsoluteUrl = avatarImagePathOrUrl.startsWith('/')
    ? `${canonicalUrl}${avatarImagePathOrUrl}`
    : avatarImagePathOrUrl;

  const twitterMetaTags = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: headline.primary },
    { name: 'twitter:image:src', content: coverImageUrl },
  ];

  if (social.twitter) {
    twitterMetaTags.push({ name: 'twitter:site', content: `@${social.twitter.split('/')[1]}` });
  }

  const allSocialUrls = values(omit(social, '__typename'))
    .filter((v) => v != null)
    .map((url) => `https://${url}`);

  const defaultSeoConfig = {
    title,
    description: headline.primary,
    openGraph: {
      url: canonicalUrl,
      images: [
        {
          url: coverImageUrl,
          alt: 'Cover Image',
          type: ImageHelpers.inferMimeType(coverImageUrl) ?? 'image/png',
          width: 1200,
          height: 630,
        },
      ],
      type: 'website',
      title,
      description: headline.primary,
      locale: 'en_US',
      siteName: title,
    },
    twitter: {
      // Others are intentionally omitted: https://github.com/garmeeh/next-seo?tab=readme-ov-file#twitter
      cardType: 'summary_large_image',
    },
    additionalLinkTags: [{ rel: 'icon', href: avatarImagePathOrUrl }],
    additionalMetaTags: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'name', content: title },
      { name: 'image', content: coverImageUrl },
    ],
  };

  return (
    <>
      <DefaultSeo {...defaultSeoConfig} />
      {/* WebSitePageJson is not supported yet, open issue: https://github.com/garmeeh/next-seo/issues/1472 */}
      <SiteLinksSearchBoxJsonLd
        scriptKey={'website'}
        type={'Website'}
        // If Google decides the website name isn't unique enough, it will use one of the fallback `alternateName` values.
        // For now, let's just fall back to the website domain (this was the default anyway).
        alternateName={[canonicalDomain]}
        name={title}
        url={canonicalUrl}
        // this is specific to SiteLinksSearchBoxJsonLd, there is no way to use JsonLd directly it seems, here is how this would work ideally:
        // potentialActions={[
        //   {
        //     target: 'https://www.yourstore.com/search?q={search_term_string}',
        //     queryInput: 'required name=search_term_string',
        //   },
        // ]}
        potentialActions={[{ target: '', queryInput: '' }]}
      />
      <OrganizationJsonLd
        name={title}
        url={canonicalUrl}
        logo={avatarImageAbsoluteUrl}
        sameAs={allSocialUrls}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Order now',
            item: `${canonicalUrl}${PageRoutes.ORDER}`,
          },
          {
            position: 2,
            name: 'About',
            item: `${canonicalUrl}${PageRoutes.ABOUT}`,
          },
          {
            position: 3,
            name: 'Plans',
            item: `${canonicalUrl}${PageRoutes.PLANS}`,
          },
          {
            position: 4,
            name: 'Gift Cards',
            item: `${canonicalUrl}${PageRoutes.GIFT_CARDS}`,
          },
          {
            position: 5,
            name: 'FAQs',
            item: `${canonicalUrl}${PageRoutes.FAQ}`,
          },
        ]}
      />
    </>
  );
};

export default ShopSEO;
